import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { BadgeCheck, Globe, Mail, MapPin } from "lucide-react";
import { Reveal, Chapter, EASE } from "./primitives";
import { useLanguage } from "../../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD =
  "w-full border-b border-kn-navy/15 bg-transparent py-3.5 text-base text-kn-navy placeholder:text-kn-navy/35 focus:border-kn-blue focus:outline-none transition-colors duration-300";

const initial = { name: "", organization: "", role: "", email: "", message: "" };

export const Contact = () => {
  const { t } = useLanguage();
  const C = t.contact;
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/demo-requests`, form);
      setStatus("sent");
    } catch (err) {
      setStatus("idle");
      setError(C.error);
    }
  };

  return (
    <section id="contact" className="relative bg-kn-ice py-24 lg:py-32" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-16 lg:grid-cols-2">
          <Reveal>
            <Chapter index="08" label={C.chapter} />
            <h2 className="mt-6 text-4xl font-semibold tracking-tight text-kn-navy sm:text-5xl">
              {C.h2}
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-kn-navy/60">
              {C.sub}
            </p>
            <div className="mt-12 space-y-6" data-testid="contact-details">
              <div className="flex items-center gap-4">
                <Globe className="h-5 w-5 text-kn-blue" strokeWidth={1.25} />
                <a href="https://www.krida.me" target="_blank" rel="noopener noreferrer" data-testid="contact-website-link" className="text-sm text-kn-navy/75 transition-colors duration-300 hover:text-kn-blue">www.krida.me</a>
              </div>
              <div className="flex items-center gap-4">
                <Mail className="h-5 w-5 text-kn-blue" strokeWidth={1.25} />
                <a href="mailto:hello@krida.me" data-testid="contact-email-link" className="text-sm text-kn-navy/75 transition-colors duration-300 hover:text-kn-blue">hello@krida.me</a>
              </div>
              <div className="flex items-center gap-4">
                <MapPin className="h-5 w-5 text-kn-blue" strokeWidth={1.25} />
                <span className="text-sm text-kn-navy/75">Singapore · Malaysia · India</span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <AnimatePresence mode="wait">
              {status === "sent" ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: EASE }}
                  className="bg-tile-gradient flex h-full min-h-[420px] flex-col items-center justify-center rounded-2xl p-12 text-center"
                  data-testid="form-success"
                >
                  <BadgeCheck className="h-12 w-12 text-kn-cyan" strokeWidth={1} />
                  <h3 className="mt-6 text-2xl font-semibold text-white">{C.successTitle}</h3>
                  <p className="mt-3 max-w-sm text-sm leading-relaxed text-white/65">
                    {C.successBody(form.name.split(" ")[0], form.organization)}
                  </p>
                  <button
                    data-testid="form-reset-button"
                    onClick={() => { setForm(initial); setStatus("idle"); }}
                    className="mt-8 font-mono text-[11px] tracking-[0.24em] uppercase text-kn-cyan hover:text-white transition-colors duration-300"
                  >
                    {C.sendAnother}
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={submit}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.5, ease: EASE }}
                  className="rounded-2xl border border-kn-navy/10 bg-white p-8 lg:p-10"
                  data-testid="demo-request-form"
                >
                  <div className="grid gap-6 sm:grid-cols-2">
                    <input required data-testid="form-name" placeholder={C.ph.name} value={form.name} onChange={set("name")} className={FIELD} />
                    <input required data-testid="form-organization" placeholder={C.ph.organization} value={form.organization} onChange={set("organization")} className={FIELD} />
                  </div>
                  <div className="mt-6 grid gap-6 sm:grid-cols-2">
                    <input required data-testid="form-role" placeholder={C.ph.role} value={form.role} onChange={set("role")} className={FIELD} />
                    <input required type="email" data-testid="form-email" placeholder={C.ph.email} value={form.email} onChange={set("email")} className={FIELD} />
                  </div>
                  <textarea
                    required
                    data-testid="form-message"
                    placeholder={C.ph.message}
                    rows={4}
                    value={form.message}
                    onChange={set("message")}
                    className={`${FIELD} mt-6 resize-none`}
                  />
                  {error && (
                    <p className="mt-4 text-sm text-red-600" data-testid="form-error">{error}</p>
                  )}
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid="form-submit-button"
                    className="mt-10 w-full rounded-full bg-brand-gradient py-4 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                  >
                    {status === "sending" ? C.sending : C.submit}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
